import type { BaseDataItem } from '@wix/thunderbolt-becky-types'
import type {
	VariantRelationViewItem,
	RefArrayViewItem,
	InflatedRefArrayViewItem,
	InflatedRefArrayViewItemValues
} from '@wix/thunderbolt-catharsis'
import { isRefArray, isVariantRelation } from './variantsUtils'

/**
 * Flattens nested structures by recursively processing RefArray and VariantRelation items
 * @param values - Array of items that may contain nested structures
 * @returns Flattened array of items
 */
export const flattenNestedStructures = <T extends BaseDataItem>(
	values: InflatedRefArrayViewItem<T>['values']
): RefArrayViewItem<T>['values'] => {
	return values.flatMap((item) => {
		if (isRefArray<T>(item)) {
			return flattenNestedStructures(item.values)
		}

		if (isVariantRelation(item)) {
			return processVariantRelation(item)
		}

		return [item as T]
	})
}

/**
 * Processes a variant relation item by flattening its nested structures
 * @param item - VariantRelation item to process
 * @returns Array of processed variant relation items
 */
const processVariantRelation = <T extends BaseDataItem>(
	item: VariantRelationViewItem<T>
): RefArrayViewItem<T>['values'] => {
	const toItem = item.to as InflatedRefArrayViewItemValues<T>
	if (!isRefArray<T>(toItem)) {
		return [item]
	}

	const nestedValues = flattenNestedStructures(toItem.values)
	return nestedValues.map((value) => ({ ...item, to: value } as VariantRelationViewItem<T>))
}
